import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useParams } from 'react-router'
import { useVariableValue } from '@devcycle/react-client-sdk'
import { useFetchDocList, useFetchSubmittals, useFetchWorkflow } from '~/hooks/submittals'
import { getStepCounter } from './workflow.utils'

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableHeader
} from '~/components/ui/Table'
import Loading from '~/components/ui/Loading'
import { DrawerLayout, DrawerContent, DrawerMainContent } from '~/components/DrawerLayout'
import ChangesSubmittalsTabs from '~/components/ChangesSubmittalsTabs'
import Button from '~/components/ui/Button'
import { MenuSimple as MenuSimpleIcon } from '~/components/ui/icons/MenuSimple'
import UploaderProductDataDocumentDialog from '~/components/Submittals/UploaderProductDataDocumentDialog'
import { getWorkflowStepResult } from '~/lib/mbue-api/services/workflowStepResult'
import { useQuery } from '@tanstack/react-query'
import { getReportUrl } from '~/lib/mbue-api/services/submittals'
import { toast } from 'sonner'
import { Loader2, DownloadIcon } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '~/components/ui/DropdownMenu'
import ConfirmationDialog from '~/components/ConfirmationDialog'

const statusColors = {
  completed: 'bg-[#34A853] text-white hover:bg-[#34A853]/90 disabled:opacity-100',
  continue: 'bg-highlight text-content hover:bg-highlight disabled:opacity-100',
  review: 'bg-gray-200'
}

const WorkFlow = () => {
  const asideRef = useRef<HTMLDivElement>(null)
  const [asideOpen, setAsideOpen] = useState<boolean>(true)
  const [uploadDocumentsOpen, setUploadDocumentsOpen] = useState<boolean>(false)
  const [isAllStepsCompleted, setIsAllStepsCompleted] = useState<boolean>(false)
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { orgId, projectId, workflowInstanceId } = useParams<{
    orgId: string
    projectId: string
    workflowInstanceId: string
  }>()

  const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false)

  const isSubmittalsEnabled = useVariableValue('streamlining-submittals-enabled', true)

  const isProcore = location.pathname.startsWith('/procore') ? '/procore' : ''
  const isSubmittalsTab = location.pathname.includes('/submittals')

  const { data: submittals = [], isLoading: isLoadingSubmittals } = useFetchSubmittals(
    { projectId: parseInt(projectId!, 10) },
    { enabled: isSubmittalsTab }
  )

  const {
    data: workflowWithData,
    isLoading: workflowWithDataIsLoading,
    refetch: refetchWorkflowWithData
  } = useFetchWorkflow(
    {
      projectId: parseInt(projectId!, 10),
      workflowInstanceId: workflowInstanceId!
    },
    {
      refetchInterval: !isAllStepsCompleted ? 10000 : false
    }
  )

  const { data: workflowReportResult, isFetched } = useQuery({
    queryKey: ['workflowReportResult', projectId, workflowInstanceId],
    queryFn: () =>
      getWorkflowStepResult({
        projectId: Number(projectId),
        workflowInstanceId: workflowInstanceId!,
        stepIndex: 5
      }),
    enabled: isAllStepsCompleted,
    refetchInterval: isAllStepsCompleted ? 10000 : false,
    retry: false
  })

  const { data: docList, isLoading: isLoadingDocList } = useFetchDocList(
    { projectId: parseInt(projectId!, 10) },
    {
      refetchInterval: false,
      retry: false
    }
  )

  const onDownloadReport = async () => {
    if (isDownloadingReport) return

    setIsDownloadingReport(true)
    try {
      const reportResult = workflowReportResult as unknown as WorkflowReportResult
      if (!reportResult?.report_files?.detailed_report) return

      const response = await getReportUrl(projectId!, workflowInstanceId!)

      if (response.report_url) {
        window.open(response.report_url, '_blank')
      }
    } catch (error) {
      toast.error('Failed to download report')
    } finally {
      setIsDownloadingReport(false)
    }
  }

  useEffect(() => {
    if (!isSubmittalsEnabled) {
      navigate(`${isProcore}/org/${orgId}/project/${projectId}/reviews`)
    }
  }, [isSubmittalsEnabled, isProcore, navigate, orgId, projectId])

  useEffect(() => {
    if (!workflowWithData) return

    const allStepsCompleted = workflowWithData.steps
      .flatMap((step) => step.state.completion_status)
      .slice(0, -1)
      .every((status) => status === true)

    setIsAllStepsCompleted(allStepsCompleted)
  }, [workflowWithData])

  useEffect(() => {
    const reportResult = workflowReportResult as WorkflowReportResult
    if (
      isAllStepsCompleted &&
      isFetched &&
      !reportResult?.report_files?.detailed_report
    ) {
      setConfirmationDialogOpen(true)
    }
  }, [isAllStepsCompleted, isFetched, workflowReportResult])

  if (!isSubmittalsTab) return null

  const isLoading = isLoadingSubmittals || workflowWithDataIsLoading || isLoadingDocList

  const stepsCompleted = workflowWithData?.steps.filter(
    (step) => step.state.status.toLowerCase() === 'completed'
  ).length

  return (
    <>
      <DrawerLayout open={asideOpen} setOpen={setAsideOpen}>
        <DrawerContent className="h-full flex flex-col w-[368px]">
          <div className="flex flex-col px-6 border-b border-divider py-4">
            <div className="">
              <ChangesSubmittalsTabs defaultTab="submittals" />
            </div>
          </div>

          <div ref={asideRef} className="overflow-y-auto flex-1">
            {submittals.length > 0 && (
              <>
                <Link to={location.pathname.split('/').slice(0, -2).join('/')}>
                  <div className="px-6 flex items-center border-b border-divider min-h-20 bg-gray-50 border-l-4 border-l-divider">
                    <h3 className="text-xl font-semibold">Division 26</h3>
                  </div>
                </Link>
                <div className="px-6 flex flex-col justify-center border-b border-divider min-h-16 bg-highlight-bg border-l-4 border-l-highlight">
                  <h4 className="text-md font-semibold">Lighting Fixtures</h4>
                  {workflowWithData?.steps.length && stepsCompleted != null && (
                    <div className="flex items-center text-xs text-disabled gap-2">
                      <p>{stepsCompleted} Completed</p>
                      <p className="text-lg font-bold">·</p>
                      <p>{workflowWithData?.steps.length - stepsCompleted - 1} Open</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DrawerContent>

        <DrawerMainContent className="w-full h-full flex flex-col">
          <div className="h-[73px] flex justify-between items-center border-b border-divider px-4">
            <div className="flex gap-2 items-cente pl-4">
              <button
                onClick={() => setAsideOpen((prevState) => !prevState)}
                className={`border border-divider rounded-full p-[3px] ${asideOpen && 'hidden'}`}
              >
                <MenuSimpleIcon />
              </button>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  className="min-w-28 capitalize gap-2"
                  disabled={!docList}
                >
                  <DownloadIcon size={16} />
                  <span>Download</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="mr-4">
                <DropdownMenuItem>
                  <Link
                    to={docList?.drawing_doc?.download_url?.[0] ?? ''}
                    target="_blank"
                  >
                    Drawings Document
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Link
                    to={docList?.specification_doc?.download_url ?? ''}
                    target="_blank"
                  >
                    Specifications Document
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="relative w-full flex-grow">
            {isLoading && <Loading position="absolute" />}

            {!isLoading && (
              <div className="w-full px-4 py-6">
                <div className="relative w-full rounded-xl border max-h-[calc(100%_-_153px)] overflow-y-auto">
                  <Table>
                    <TableHeader className="bg-gray-50 sticky top-0 z-10">
                      <TableRow>
                        <TableHead>Steps</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <>
                        {workflowWithData?.steps.slice(0, -1).map((step, index) => {
                          const {
                            label: buttonLabel,
                            disabled: buttonDisabled,
                            className: buttonClassName
                          } = stepReady(step)

                          return (
                            <TableRow key={step.name}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{step.name}</TableCell>
                              <TableCell>
                                {getStepCounter(step.state.completion_status)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant={buttonDisabled ? 'secondary' : 'default'}
                                  className={`min-w-28 capitalize ${buttonClassName}`}
                                  disabled={buttonDisabled}
                                  onClick={() => {
                                    switch (step.step_type) {
                                      case 'upload':
                                        setUploadDocumentsOpen(true)
                                        break
                                      case 'bounding_box':
                                        navigate(
                                          `${location.pathname}/schedule-selection`
                                        )
                                        break
                                      case 'table_extraction':
                                        navigate(`${location.pathname}/confirm-products`)
                                        break
                                      case 'list_extraction':
                                        navigate(
                                          `${location.pathname}/confirm-product-requirements`
                                        )
                                        break
                                      case 'page_alignment':
                                        navigate(
                                          `${location.pathname}/confirm-matched-products`
                                        )
                                        break
                                      default:
                                        return
                                    }
                                  }}
                                >
                                  {buttonLabel}
                                </Button>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell colSpan={2}>
                            Lighting Fixtures Submittal Report
                          </TableCell>
                          <TableCell>
                            <Button
                              onClick={() => void onDownloadReport()}
                              className="min-w-28 capitalize"
                              disabled={getReportButtonDisabled(
                                isAllStepsCompleted,
                                isFetched,
                                workflowReportResult
                              )}
                              variant={
                                getReportButtonDisabled(
                                  isAllStepsCompleted,
                                  isFetched,
                                  workflowReportResult
                                )
                                  ? 'secondary'
                                  : 'default'
                              }
                            >
                              {getReportButtonLabel(
                                isAllStepsCompleted,
                                isFetched,
                                workflowReportResult
                              ) === 'Generating' && (
                                <Loader2
                                  size={16}
                                  strokeWidth={3}
                                  className="animate-spin mr-1"
                                />
                              )}
                              {getReportButtonLabel(
                                isAllStepsCompleted,
                                isFetched,
                                workflowReportResult
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      </>
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </DrawerMainContent>
      </DrawerLayout>
      <UploaderProductDataDocumentDialog
        open={uploadDocumentsOpen}
        setOpen={setUploadDocumentsOpen}
        onDocListCreated={() => void refetchWorkflowWithData()}
      />
      <ConfirmationDialog
        title="All Done!"
        open={confirmationDialogOpen}
        onOpenChange={setConfirmationDialogOpen}
        onConfirm={() => setConfirmationDialogOpen(false)}
        confirmLabel="Close"
      >
        We will take it from here. You will receive an email when the submittals report is
        generated.
        <br />
        You can close your browser. get some coffee, and come back, or click
        &apos;Close&apos; to continue working on the platform while we generate your
        report.
      </ConfirmationDialog>
    </>
  )
}

export default WorkFlow

interface Step {
  state: {
    status: string
    completion_status: boolean[] | boolean | null
  }
}

function stepReady(step: Step): { label: string; disabled: boolean; className: string } {
  const { status, completion_status } = step.state
  const completionStatus = Array.isArray(completion_status)
    ? completion_status
    : [completion_status]
  const areAllTasksCompleted = completionStatus.every((taskStatus) => taskStatus === true)

  if (status === 'Completed' && areAllTasksCompleted) {
    return { label: 'Completed', disabled: false, className: statusColors.completed }
  }

  if (status === 'Completed' && !areAllTasksCompleted) {
    return { label: 'Continue', disabled: false, className: statusColors.continue }
  }

  if (status === 'Waiting') {
    return { label: 'Pending', disabled: true, className: '' }
  }

  // status === 'Ready'
  return {
    label: 'Start',
    disabled: false,
    className: ''
  }
}

interface WorkflowReportResult {
  report_files?: {
    detailed_report: string
  }
}

function getReportButtonLabel(
  isAllStepsCompleted: boolean,
  isFetched: boolean,
  workflowReportResult?: unknown
) {
  const reportResult = workflowReportResult as WorkflowReportResult
  if (isAllStepsCompleted && isFetched && !reportResult?.report_files?.detailed_report) {
    return 'Generating'
  }

  return 'Download'
}

function getReportButtonDisabled(
  isAllStepsCompleted: boolean,
  isFetched: boolean,
  workflowReportResult?: unknown
) {
  const reportResult = workflowReportResult as WorkflowReportResult
  if (isAllStepsCompleted && reportResult?.report_files?.detailed_report) {
    return false
  }

  return true
}
